import { createRef, useState } from 'react';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { Accordion, Button, Form } from 'react-bootstrap';
import AccordionItem from 'components/AccordionItem';
import substringSearch from 'utils/substring-search';
import styles from './ExpandableList.module.scss';

const NO_ITEMS_FOUND_MESSAGE = 'No items found';

export function ExpandableList({
  enable_search: enableSearch,
  header,
  items = [],
}) {
  const formRef = createRef();
  const [displayItems, setDisplayItems] = useState(items);

  const search = debounce((searchTerm) => {
    if (!searchTerm) {
      setDisplayItems(items);
      return;
    }

    const searchedListItems = new Set(
      substringSearch(
        searchTerm.toLowerCase(),
        items.map(
          (item) => `${item.header.toLowerCase()} ${item.body.toLowerCase()}`
        )
      )
    );

    const filteredListItems = items.filter((item) =>
      searchedListItems.has(
        `${item.header.toLowerCase()} ${item.body.toLowerCase()}`
      )
    );

    setDisplayItems(filteredListItems);
  }, 500);

  const handleSearch = (event) => {
    event.stopPropagation();
    event.preventDefault();
    search(event.target.value);
    return false;
  };

  const handleClear = (event) => {
    event.stopPropagation();
    event.preventDefault();
    formRef?.current?.reset();
    search('');
  };

  const descriptionEventKey = '0';
  return items.length > 0 ? (
    <section className="d-flex flex-column align-items-center mx-auto mw-1000 p-10 text-primary">
      {header && <h2>{header}</h2>}
      {enableSearch && (
        <div className="w-100 w-md-75 my-5">
          <Form
            ref={formRef}
            id="list_search_form"
            className="w-100 d-flex rounded-2 border border-2 border-primary"
            onSubmit={(event) => {
              event.stopPropagation();
              event.preventDefault();
              return false;
            }}
          >
            <Form.Control
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className={classNames(
                'bg-transparent text-decoration-none w-100',
                styles.search_input
              )}
              id="list_search_term"
              type="search"
              placeholder="Search"
              onChange={handleSearch}
            />

            <Button
              aria-label="Clear Search Term"
              className={classNames(
                'ms-4 h-100',
                styles.clear_search_icon_wrapper
              )}
              variant="none"
              type="button"
              onClick={handleClear}
            >
              <div className={classNames(styles.clear_search_icon)}>
                <FontAwesomeIcon icon={faXmark} />
              </div>
            </Button>
          </Form>
        </div>
      )}
      {displayItems?.length > 0 ? (
        <Accordion
          flush
          defaultActiveKey={descriptionEventKey}
          className="w-100"
        >
          {displayItems.map((item, idx) => {
            const { header, body } = item;
            return (
              <AccordionItem
                key={header ?? `expandable_list_section_${idx}`}
                renderBodyAsHtml
                eventKey={idx.toString()}
                header={header}
                body={body}
              />
            );
          })}
        </Accordion>
      ) : (
        <span>{NO_ITEMS_FOUND_MESSAGE}</span>
      )}
    </section>
  ) : null;
}

export default ExpandableList;
